import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Avatar,
  Stack,
  Button,
  Checkbox,
  TextField,
  MenuItem,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  CircularProgress
} from "@mui/material";

import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import Swal from "sweetalert2";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 24;

export default function PrintPage() {
  const [view, setView] = useState("card");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [demoData, setDemoData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  const [search, setSearch] = useState("");
  const [cls, setCls] = useState("");
  const [section, setSection] = useState("");
  const [sort, setSort] = useState("asc");
  const navigate = useNavigate();

  const scrollRef = useRef(null);
  const loaderRef = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  async function fetchStudentRecords() {
    setLoading(true);
    try {
      const formId = localStorage.getItem("schoolId");
      const response = await axios.post(`${API_BASE_URL}/get-school-wise-printed-list`, { formId });
      if (response?.data?.status === "SUCCESS") {
        setDemoData(response.data.students || []);
        setTotalRecords(response.data.students?.length || 0);
      } else if (response?.data?.status === "JWT_INVALID") {
        Swal.fire({ text: response?.data?.message, icon: "warning" })
      } else {
        Swal.fire({ text: response?.data?.message, icon: "warning" })
      }
    } catch (error) {
      Swal.fire({ text: error?.message, icon: "warning", })
    } finally {
      setLoading(false);
    }
  }

  /* ---------------- FILTER + SORT ---------------- */
  const filtered = useMemo(() => {
    let d = [...demoData];

    // Search
    if (search) {
      const searchText = search.toLowerCase().trim();

      d = d.filter((s) =>
        Object.values(s.resData || {}).some((value) =>
          String(value).toLowerCase().includes(searchText)
        )
      );
    }

    // Class Filter
    if (cls) {
      d = d.filter((s) => s.resData?.Class === cls);
    }

    // Section Filter
    if (section) {
      d = d.filter((s) => s.resData?.Section === section);
    }

    // Sort
    d.sort((a, b) => {
      const getSortValue = (obj) => {
        // Try to find a field containing "name"
        const nameKey = Object.keys(obj || {}).find((key) =>
          key.toLowerCase().includes("name")
        );

        if (nameKey) {
          return String(obj[nameKey] || "").trim();
        }

        // Fallback: use first string value
        const firstValue = Object.values(obj || {}).find(
          (value) => typeof value === "string"
        );

        return String(firstValue || "").trim();
      };

      const valueA = getSortValue(a.resData).toLowerCase();
      const valueB = getSortValue(b.resData).toLowerCase();

      return sort === "asc"
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    });

    return d;
  }, [search, cls, section, sort, demoData]);

  const classOptions = useMemo(() => {
    const classes = demoData
      .map((s) => s.resData?.Class)
      .filter(Boolean);

    return [...new Set(classes)];
  }, [demoData]);

  const sectionOptions = useMemo(() => {
    const sections = demoData
      .map((s) => s.resData?.Section)
      .filter(Boolean);

    return [...new Set(sections)];
  }, [demoData]);

  /* ---------------- INFINITE SCROLL ---------------- */
  const visibleData = useMemo(
    () => filtered.slice(0, page * PAGE_SIZE),
    [filtered, page]
  );

  useEffect(() => {
    if (!scrollRef.current || !loaderRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && visibleData.length < filtered.length) {
          setPage((p) => p + 1);
        }
      },
      { root: scrollRef.current, threshold: 0 }
    );

    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [visibleData.length, filtered.length]);

  /* ---------------- SELECTION ---------------- */
  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const selectAll = () =>
    setSelected(new Set(visibleData.map((s) => s._id)));

  const clearSelection = () => setSelected(new Set());

  /* ---------------- ACTIONS ---------------- */
  const handleEdit = (record) => {
    alert(`Edit record ${record._id}`);
  };

  useEffect((() => {
    fetchStudentRecords();
  }), []);


  /* ---------------- TABLE VIEW ---------------- */
  const TableView = () => (
    <Card>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={
                  selected.size > 0 &&
                  selected.size === visibleData.length
                }
                indeterminate={
                  selected.size > 0 &&
                  selected.size < visibleData.length
                }
                onChange={(e) =>
                  e.target.checked ? selectAll() : clearSelection()
                }
              />
            </TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Class</TableCell>
            <TableCell>Mobile</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {visibleData.map((s) => (
            <TableRow key={s._id} hover>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selected.has(s._id)}
                  onChange={() => toggle(s._id)}
                />
              </TableCell>
              <TableCell>{s.resData?.Name}</TableCell>
              <TableCell>{s.resData?.Class}</TableCell>
              <TableCell>{s.resData?.["Mobile No"]}</TableCell>
              <TableCell>
                <Chip size="small" label="Ready to Print" color="warning" />
              </TableCell>
              {/* <TableCell align="right">
                <IconButton onClick={() => handleEdit(s)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  color="error"
                  // onClick={() => handleDelete(s._id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell> */}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );

  /* ---------------- CARD VIEW ---------------- */
  const CardView = () => (
    <Grid container spacing={3}>
      {visibleData.map((s) => {
        const d = s.resData || {};
        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={s._id}>
            <Card
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow:
                  "0 10px 25px rgba(0,0,0,0.08)",
                transition: "0.3s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow:
                    "0 16px 35px rgba(0,0,0,0.15)"
                }
              }}
            >
              {/* TOP STRIP */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  background:
                    "linear-gradient(135deg,#667eea,#764ba2)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}
              >
                <Typography
                  fontWeight={600}
                  fontSize={14}
                  sx={{ color: '#FFFFFF' }}
                >
                  School Print
                </Typography>

                {/* <Stack direction="row" spacing={0.5}>
                  <Checkbox
                    size="small"
                    checked={selected.has(s._id)}
                    onChange={() => toggle(s._id)}
                    sx={{ color: "#fff" }}
                  />
                  <IconButton
                    size="small"
                    sx={{ color: "#fff" }}
                    onClick={() => handleEdit(s)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ color: "#fff" }}
                    // onClick={() => handleDelete(s._id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack> */}
              </Box>

              {/* BODY */}
              <Box p={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    src={`${process.env.REACT_APP_IMG_URL}/${s.uploadImgRes?.[0]?.fileUrl}`}
                    variant="rounded"
                    sx={{
                      width: 90,
                      height: 110,
                      borderRadius: 2,
                      border: "2px solid #eee"
                    }}
                  />

                  <Box flex={1}>
                    <Typography
                      fontWeight={700}
                      fontSize={16}
                      noWrap={false}
                      sx={{
                        wordBreak: "break-word",
                        overflowWrap: "anywhere"
                      }}
                    >
                      {d.Name || "—"}
                    </Typography>

                    {d.Class && (
                      <Typography fontSize={13} color="text.secondary">
                        Class: {d.Class} {d.Section ? `(${d.Section})` : ""}
                      </Typography>
                    )}

                    {d["Date of Birth"] && (
                      <Typography fontSize={13}>
                        DOB: {d["Date of Birth"]}
                      </Typography>
                    )}

                    {d["Mobile No"] && (
                      <Typography fontSize={13}>
                        Mobile: {d["Mobile No"]}
                      </Typography>
                    )}
                  </Box>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                {/* DYNAMIC EXTRA FIELDS */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1
                  }}
                >
                  {Object.entries(d)
                    .filter(
                      ([k]) =>
                        ![
                          "Name",
                          "Class",
                          "Section",
                          "Date of Birth",
                          "Mobile No",
                          "Profile",
                        ].includes(k)
                    )
                    .slice(0, 4)
                    .map(([k, v]) => (
                      <Typography
                        key={k}
                        fontSize={12}
                        sx={{
                          background: "#f5f7ff",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1
                        }}
                      >
                        <b>{k}:</b> {v}
                      </Typography>
                    ))}
                </Box>

                <Box mt={1}>
                  <Chip
                    size="small"
                    label="Ready to Print"
                    color="warning"
                  />
                </Box>
              </Box>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );


  /* ---------------- UI ---------------- */
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100%',
          backgroundColor: '#f4f4f4',
        }}
      >
        <div className="loader">
          {[1, 2, 3, 4].map((num) => (
            <div key={num} className={`box box-${num}`}>
              <div className="side-left"></div>
              <div className="side-right"></div>
              <div className="side-top"></div>
            </div>
          ))}
        </div>
      </Box>
    );
  }

  return (
    <Box>

      {/* FILTER BAR */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
        >
          <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">

            <TextField
              size="small"
              label="Search Name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ marginTop: 3 }}
            />
            <TextField
              size="small"
              select
              label="Class"
              value={cls}
              onChange={(e) => setCls(e.target.value)}
              sx={{ minWidth: 120 }}
              style={{ marginTop: 3 }}

            >
              <MenuItem value="">All</MenuItem>
              {classOptions.map((className) => (
                <MenuItem key={className} value={className}>
                  {className}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label="Section"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              sx={{ minWidth: 100 }}
              style={{ marginTop: 3 }}>
              <MenuItem value="">All</MenuItem>
              {sectionOptions.map((sectionName) => (
                <MenuItem key={sectionName} value={sectionName}>
                  {sectionName}
                </MenuItem>
              ))}
            </TextField>

            <IconButton onClick={() => setSort(sort === "asc" ? "desc" : "asc")}>
              {sort === "asc" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
            </IconButton>

            <IconButton onClick={() => setView("card")}>
              <ViewModuleIcon />
            </IconButton>
            <IconButton onClick={() => setView("table")}>
              <TableRowsIcon />
            </IconButton>
            {/* <Button onClick={selectAll}>Select All</Button> */}
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={`Total: ${filtered.length}`}
              color="warning"
              sx={{
                fontWeight: "bold",
                fontSize: "0.9rem",
                px: 1,
              }}
            />
          </Stack>
        </Stack>
      </Card>

      {/* SCROLL AREA */}
      <Box
        ref={scrollRef}
        sx={{
          height: "calc(100vh - 220px)",
          overflowY: "auto",
          p: 1
        }}
      >
        {view === "card" ? <CardView /> : <TableView />}
        <Box ref={loaderRef} height={60} />
      </Box>
    </Box >
  );
}